/**
 * One past generation: thumbnail, metadata, publication tracking and the four actions.
 *
 * Ported from `components/history/HistoryItem.tsx`, and shared by `/history` and
 * `/admin/history` for the reason that file gives: the only difference between the two views
 * is a client-name label, so one component cannot drift from itself. That sharing is also why
 * an admin can delete any client's generation here — `/generate/delete` authorises
 * owner-or-admin, exactly as download and the integrity record do.
 *
 * **What React Query changed, and what it did not.** The Next.js card mirrored publication
 * state locally after a successful publish, because its parent owned a fetched array with no
 * way to patch one item. That mirror is gone: the mutation invalidates every list showing
 * publication state and the card re-renders from the refetched document, so what is on screen
 * is what the server holds rather than an optimistic guess that a reload would correct.
 *
 * What stayed is every piece of behaviour that is about *this* card rather than about
 * fetching:
 *
 * - **A failed thumbnail says the link expired, not that the image is gone.** Preview URLs are
 *   short-lived signed URLs minted when the page loaded. The browser's own broken-image glyph
 *   would suggest the stored object had been deleted, when it is fine and only the link went
 *   stale.
 * - **The prompt is clamped, with a toggle only when there is something to reveal.** A prompt
 *   that already fits gets no control, because clicking it would change nothing.
 * - **Delete is behind a confirmation that keeps its own error.** A failure leaves the dialog
 *   open so the message appears next to the button that produced it.
 */

import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import ImageNotSupportedOutlined from "@mui/icons-material/ImageNotSupportedOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { downloadGeneration, downloadIntegrityRecord, viewIntegrityRecord } from "@/api/downloads";
import type { GenerationWithPreview } from "@/api/queries";
import { usePublishGeneration, useWithdrawGeneration } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import {
  MODE_LABELS,
  PRINT_SIZE_LABELS,
  RATIO_ASPECT_CSS,
  RATIO_LABELS,
} from "@/constants/imageOutput";
import { GLOW_FRAME } from "@/theme/tokens";

const DELETE_FAILURE_MESSAGE = "Couldn't delete this generation. Please try again.";
const PUBLICATION_FAILURE_MESSAGE = "Couldn't save that. Please try again.";

/**
 * Above this many characters a prompt is assumed to overflow the three-line clamp and gets the
 * expand toggle. An estimate, not a measurement — measuring would need a layout read per card
 * — and deliberately generous, since an unnecessary toggle is a smaller annoyance than a
 * silently truncated prompt with no way to see the rest.
 */
const PROMPT_CLAMP_THRESHOLD_CHARS = 140;

export interface GenerationCardProps {
  generation: GenerationWithPreview;
  /** Admin view only: whose generation this is. */
  showClientName?: boolean;
  /**
   * Deletes the generation and refreshes whichever lists the caller is showing. Passed in
   * rather than chosen here because `/history` and `/admin/history` invalidate different
   * queries, and a card deciding that would be a card that knows which page it is on.
   */
  onDelete: (generationId: string) => Promise<unknown>;
}

export function GenerationCard({
  generation,
  showClientName = false,
  onDelete,
}: GenerationCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  return (
    <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {generation.thumbnailUrl && !previewFailed ? (
        <Box
          component="img"
          src={generation.thumbnailUrl}
          alt={generation.altText ?? generation.finalPrompt ?? "Generated image"}
          loading="lazy"
          onError={() => setPreviewFailed(true)}
          sx={{
            ...GLOW_FRAME,
            width: "100%",
            aspectRatio: RATIO_ASPECT_CSS[generation.ratio],
            objectFit: "cover",
            backgroundColor: "background.default",
          }}
        />
      ) : (
        <MissingPreview
          generation={generation}
          reason={previewFailed ? "expired" : "gone"}
        />
      )}

      <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 1.5 }}>
        <MetadataChips generation={generation} />

        {showClientName ? (
          <Typography variant="subtitle2" noWrap title={generation.clientName}>
            {generation.clientName || "Unknown client"}
          </Typography>
        ) : null}

        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            className={expanded ? undefined : "line-clamp-3"}
          >
            {generation.finalPrompt || "No prompt recorded."}
          </Typography>
          {generation.finalPrompt.length > PROMPT_CLAMP_THRESHOLD_CHARS ? (
            <Button
              size="small"
              variant="text"
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
              sx={{ px: 0, minWidth: 0 }}
            >
              {expanded ? "Show less" : "Show full prompt"}
            </Button>
          ) : null}
        </Box>

        <PublicationPanel generation={generation} />

        {/* Pushed to the bottom so a row of cards with different prompt lengths still lines
            its actions up. */}
        <Box sx={{ mt: "auto", pt: 1 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: "block" }}>
            {formatTimestamp(generation.createdAt)}
          </Typography>
          <CardActions generation={generation} onDelete={onDelete} />
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * The two ways a thumbnail can be absent, told apart because the user can act on one of them.
 *
 * `gone` — the API omitted the URL, so the object itself has been swept and reloading changes
 * nothing. `expired` — the URL was there but the browser could not load it, which for a signed
 * URL almost always means it aged out while the page sat open, and a reload fixes it.
 */
function MissingPreview({
  generation,
  reason,
}: {
  generation: GenerationWithPreview;
  reason: "expired" | "gone";
}) {
  return (
    <Box
      className="flex flex-col items-center justify-center gap-1.5 bg-jp-mist/60 p-4 text-center"
      sx={{ aspectRatio: RATIO_ASPECT_CSS[generation.ratio] }}
    >
      <ImageNotSupportedOutlined fontSize="small" sx={{ color: "text.secondary" }} />
      <Typography variant="caption" color="text.secondary">
        {reason === "expired"
          ? "Preview link expired, reload to refresh it."
          : "Image no longer available"}
      </Typography>
    </Box>
  );
}

function MetadataChips({ generation }: { generation: GenerationWithPreview }) {
  const output =
    generation.outputType === "print" && generation.printSize
      ? `Print · ${PRINT_SIZE_LABELS[generation.printSize]}`
      : "Digital";

  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
      <Chip size="small" label={`${RATIO_LABELS[generation.ratio]} · ${generation.size}`} />
      <Chip size="small" label={MODE_LABELS[generation.mode]} />
      <Chip size="small" label={output} />
      {/* Only for a batch of more than one: a lone generation's "1 of 1" is noise. */}
      {generation.variationCount > 1 ? (
        <Chip
          size="small"
          label={`Option ${generation.variationIndex} of ${generation.variationCount}`}
        />
      ) : null}
      {generation.guardrailFlagged ? (
        <Chip size="small" color="warning" label="Sensitive topic" />
      ) : null}
      {/* Automated QA is informational — it never blocked anything, it flags for a look. */}
      {generation.qaFlagged ? (
        <Chip size="small" color="warning" label="Possible AI artefact" />
      ) : null}
    </Stack>
  );
}

/**
 * Publication tracking: who published this, where, and whether it has since been withdrawn.
 *
 * Collapsed by default. It is governance metadata rather than something a user acts on every
 * visit, and a form open on every card in a grid would bury the images.
 */
function PublicationPanel({ generation }: { generation: GenerationWithPreview }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(generation.publicationLabel ?? "");
  const [url, setUrl] = useState(generation.publicationUrl ?? "");

  const publish = usePublishGeneration();
  const withdraw = useWithdrawGeneration();
  const saving = publish.isPending || withdraw.isPending;
  const failed = publish.isError || withdraw.isError;

  const published = Boolean(generation.publishedAt);
  const withdrawn = Boolean(generation.withdrawnAt);

  const status = withdrawn ? " · Withdrawn" : published ? " · Published" : "";

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        sx={{ px: 0, minWidth: 0 }}
      >
        {open ? "Hide publication details" : "Publication details"}
        {status}
      </Button>

      <Collapse in={open} unmountOnExit>
        <Stack
          spacing={1.5}
          sx={{ mt: 1, p: 1.5, backgroundColor: "rgba(255,255,255,0.7)" }}
        >
          {published ? (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                <strong>{withdrawn ? "Withdrawn" : "Published"}</strong>{" "}
                {formatTimestamp(generation.publishedAt)}
                {generation.publishedBy ? ` by ${generation.publishedBy}` : ""}
              </Typography>
              {generation.publicationLabel ? (
                <Typography variant="caption" color="text.secondary">
                  Publication: {generation.publicationLabel}
                </Typography>
              ) : null}
              {generation.publicationUrl ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Location:{" "}
                  {/*
                    `noopener noreferrer` because the URL is whatever a client typed in: an
                    unguarded `_blank` hands the opened page a reference back to this one.
                  */}
                  <Link
                    href={generation.publicationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {generation.publicationUrl}
                  </Link>
                </Typography>
              ) : null}
              {withdrawn ? (
                <Typography variant="caption" color="text.secondary">
                  Withdrawn: {formatTimestamp(generation.withdrawnAt)}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label="Publication (optional)"
                placeholder="e.g. Annual report 2026"
                value={label}
                disabled={saving}
                onChange={(event) => setLabel(event.target.value)}
              />
              <TextField
                size="small"
                label="Page or URL (optional)"
                value={url}
                disabled={saving}
                onChange={(event) => setUrl(event.target.value)}
              />
            </Stack>
          )}

          {failed ? (
            <Typography role="alert" variant="caption" color="error">
              {PUBLICATION_FAILURE_MESSAGE}
            </Typography>
          ) : null}

          {published && !withdrawn ? (
            <Button
              size="small"
              variant="outlined"
              loading={saving}
              onClick={() => withdraw.mutate(generation.id)}
            >
              Withdraw
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              loading={saving}
              onClick={() =>
                publish.mutate({
                  generationId: generation.id,
                  publicationLabel: label,
                  publicationUrl: url,
                })
              }
            >
              {withdrawn ? "Publish again" : "Mark as published"}
            </Button>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

/**
 * Download, the integrity record (view and save), and delete.
 *
 * These are file transfers and a destructive action rather than cached data, so they are
 * plain async handlers with local pending state rather than React Query mutations — there is
 * no server state here for a cache to hold. Delete is the exception and is a mutation at the
 * call site, which is why it arrives as a prop.
 */
function CardActions({
  generation,
  onDelete,
}: {
  generation: GenerationWithPreview;
  onDelete: (generationId: string) => Promise<unknown>;
}) {
  const { idToken } = useAuth();
  const [busy, setBusy] = useState<"image" | "view-record" | "save-record" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function run(kind: NonNullable<typeof busy>, work: () => Promise<void>, failure: string) {
    setBusy(kind);
    setActionError(null);
    try {
      await work();
    } catch {
      setActionError(failure);
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(generation.id);
      setConfirmingDelete(false);
    } catch {
      // Left open, so the message appears next to the button that produced it rather than
      // behind a dialog that has already closed.
      setDeleteError(DELETE_FAILURE_MESSAGE);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      {actionError ? (
        <Typography role="alert" variant="caption" color="error" gutterBottom sx={{ display: "block" }}>
          {actionError}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button
          size="small"
          variant="outlined"
          loading={busy === "image"}
          onClick={() =>
            void run(
              "image",
              () => downloadGeneration(generation.id, idToken),
              "Download failed, try again.",
            )
          }
        >
          Download
        </Button>
        <Button
          size="small"
          variant="outlined"
          loading={busy === "view-record"}
          onClick={() =>
            void run(
              "view-record",
              () => viewIntegrityRecord(generation.id, idToken),
              "Couldn't open the record, try again.",
            )
          }
        >
          View record
        </Button>
        <Button
          size="small"
          variant="outlined"
          loading={busy === "save-record"}
          onClick={() =>
            void run(
              "save-record",
              () => downloadIntegrityRecord(generation.id, idToken),
              "Record download failed, try again.",
            )
          }
        >
          Save record
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteOutlined />}
          disabled={deleting}
          onClick={() => setConfirmingDelete(true)}
        >
          Delete
        </Button>
      </Stack>

      <Dialog
        open={confirmingDelete}
        onClose={() => (deleting ? undefined : setConfirmingDelete(false))}
      >
        <DialogTitle>Delete this generation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the image and its record from history. This cannot be
            undone.
          </DialogContentText>
          {deleteError ? (
            <Typography role="alert" variant="body2" color="error" sx={{ mt: 2 }}>
              {deleteError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" loading={deleting} onClick={() => void handleDelete()}>
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** ISO string → locale date and time. Tolerates a missing or malformed timestamp. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "Unknown date";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return date.toLocaleString();
}

/** ISO string → "12 Mar 2026". The date-only form, for tables where time adds nothing. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "Unknown date";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
